import { Button } from "@/components/ui/button";
import Link from "next/link";
import { BiLogoTelegram } from "react-icons/bi";
import { FaGlobe, FaXTwitter } from "react-icons/fa6";
import { motion } from "framer-motion";
import { useState } from "react";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { MdArrowOutward } from "react-icons/md";

interface LeaderCardProps {
  imageSrc: string;
  name: string;
  twitter: string;
  telegram: string;
  website: string;
  content: string;
  buy: string;
}
export function LeaderCard({
  imageSrc,
  buy,
  name,
  twitter,
  telegram,
  website,
  content,
}: LeaderCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative group bg-white w-80  rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
    >
      {/* Image/GIF Section */}
      <div className="h-48 w-full overflow-hidden">
        <motion.img
          src={imageSrc}
          alt={name}
          className="object-cover w-full h-full"
          animate={{ scale: isHovered ? 1.1 : 1 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Content Section with Flex Layout */}
      <div className="px-6 pt-4 pb-6 h-[calc(100%-12rem)] flex flex-col">
        {/* Name and Social Icons */}
        <div className="mb-4">
          <div className="flex items-center justify-between gap-1">
            <h4 className="text-lg font-bold text-indigo-950">{name}</h4>
            <div className="flex space-x-3">
              {telegram && (
                <motion.a
                  href={telegram}
                  whileHover={{ y: -2 }}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-indigo-100 hover:text-indigo-700 text-indigo-950"
                >
                  <BiLogoTelegram className="w-4 h-4" />
                </motion.a>
              )}
              {twitter && (
                <motion.a
                  href={twitter}
                  whileHover={{ y: -2 }}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-blue-100 hover:text-indigo-700 text-indigo-950"
                >
                  <FaXTwitter className="w-4 h-4" />
                </motion.a>
              )}
              {website && (
                <motion.a
                  href={website}
                  whileHover={{ y: -2 }}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-blue-100 hover:text-indigo-700 text-indigo-950"
                >
                  <FaGlobe className="w-4 h-4" />
                </motion.a>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content Text */}
        <ScrollArea className="flex-grow overflow-auto ">
          <motion.p
            initial={{ opacity: 0.8 }}
            animate={{ opacity: isHovered ? 1 : 0.8 }}
            className="text-gray-700 text-sm leading-tight transition-opacity duration-300 text-justify"
          >
            {content}
          </motion.p>
        </ScrollArea>

        {/* Buy Button - Positioned Bottom */}
        <motion.div
          className="mt-auto pt-4"
          animate={{ y: isHovered ? 0 : 5, opacity: isHovered ? 1 : 0.9 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Link href={buy} target="_blank">
            <Button
              size="lg"
              className="w-full bg-indigo-400 hover:bg-zinc-700 rounded-xl font-semibold text-white shadow-lg hover:shadow-gray-500/20 transition-all"
              // className="w-full bg-gray-900 hover:bg-gray-800 rounded-xl font-semibold text-white shadow-lg hover:shadow-gray-500/20 transition-all"
            >
              <span className="mr-2">Buy Now</span>
              <MdArrowOutward className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Hover Border Effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none border-2 rounded-3xl"
        animate={{
          borderColor: isHovered ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0)",
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}
